import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ThemeToggle } from "@/components/ThemeToggle";
import { SeoHead } from "@/components/SeoHead";
import { NAV, AUTH } from "@/constants/testIds";
import { useSession, formatApiErrorDetail } from "@/context/AuthContext";
import { MagicLinkForm } from "@/components/auth/MagicLinkForm";
import { SemaLogo } from "@/components/marketing/SemaLogo";

export default function Login() {
  const navigate = useNavigate();
  const { login, resendVerification } = useSession();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [unverified, setUnverified] = useState(false);
  const [resendMsg, setResendMsg] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setUnverified(false);
    setResendMsg("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/dashboard");
    } catch (err) {
      if (err.response?.status === 403) {
        setUnverified(true);
      }
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const onResend = async () => {
    setResendMsg("");
    try {
      const res = await resendVerification(email);
      setResendMsg(res.verification_link
        ? "Verification link sent. Test mode: check backend logs / your inbox."
        : "Verification link sent — check your inbox.");
    } catch (err) {
      setResendMsg(formatApiErrorDetail(err.response?.data?.detail) || "Could not resend. Try again shortly.");
    }
  };

  return (
    <div
      data-testid={AUTH.loginRoot}
      className="relative min-h-screen w-full bg-background text-foreground"
    >
      <SeoHead title="Sign In | Sema" description="Sign in to your Sema workspace." path="/login" noindex />
      <div
        aria-hidden="true"
        className="sema-grid pointer-events-none absolute inset-0 opacity-40"
      />

      <header className="relative z-10 mx-auto flex w-full max-w-6xl items-center justify-between px-6 py-6">
        <SemaLogo to="/" testid={NAV.brand} imgClassName="h-8" />
        <ThemeToggle />
      </header>

      <main className="relative z-10 mx-auto flex min-h-[calc(100vh-160px)] w-full max-w-md flex-col justify-center px-6">
        <p className="mb-5 inline-flex w-fit items-center gap-2 rounded-full border border-border/60 bg-card/60 px-3 py-1 font-mono text-[11px] uppercase tracking-[0.22em] text-muted-foreground backdrop-blur">
          <span className="inline-block h-1.5 w-1.5 rounded-full bg-accent" />
          sign in
        </p>

        <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">Welcome back.</h1>
        <div aria-hidden="true" className="sema-accent-underline mt-5" />

        <form onSubmit={onSubmit} className="mt-8 space-y-5">
          <div className="space-y-2">
            <Label htmlFor="email" className="font-mono text-xs uppercase tracking-[0.18em] text-muted-foreground">
              Email
            </Label>
            <Input
              id="email"
              type="email"
              required
              autoComplete="email"
              data-testid={AUTH.loginEmail}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.com"
              className="h-11 bg-card/60"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="password" className="font-mono text-xs uppercase tracking-[0.18em] text-muted-foreground">
              Password
            </Label>
            <Input
              id="password"
              type="password"
              required
              autoComplete="current-password"
              data-testid={AUTH.loginPassword}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className="h-11 bg-card/60"
            />
          </div>

          {error && (
            <p data-testid={AUTH.loginError} className="text-sm text-destructive">
              {error}
            </p>
          )}

          {unverified && (
            <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 p-4">
              <p className="text-sm text-foreground">
                Your email isn't verified yet. Resend the verification link to{" "}
                <span className="font-medium">{email}</span>?
              </p>
              <div className="mt-3 flex flex-wrap items-center gap-3">
                <Button
                  type="button"
                  variant="outline"
                  data-testid={AUTH.loginResend}
                  onClick={onResend}
                  className="rounded-full border-border/70 font-mono text-[11px] uppercase tracking-[0.2em]"
                >
                  Resend verification
                </Button>
                {resendMsg && (
                  <span data-testid={AUTH.loginResendMsg} className="text-xs text-muted-foreground">
                    {resendMsg}
                  </span>
                )}
              </div>
            </div>
          )}

          <Button
            type="submit"
            disabled={loading}
            data-testid={AUTH.loginSubmit}
            className="group h-12 w-full rounded-full sema-cta-gradient font-mono text-xs uppercase tracking-[0.22em] text-white shadow-lg shadow-sema-primary/25 hover:-translate-y-0.5 transition-transform"
          >
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>
                Sign in
                <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </>
            )}
          </Button>
        </form>

        <MagicLinkForm />

        <p className="mt-6 text-sm text-muted-foreground">
          No account?{" "}
          <Link
            to="/signup"
            data-testid={AUTH.loginToSignup}
            className="text-accent hover:underline"
          >
            Create one
          </Link>
        </p>

        <Link
          to="/"
          className="mt-10 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.22em] text-muted-foreground hover:text-accent transition-colors"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          back to home
        </Link>
      </main>
    </div>
  );
}
